import type { DataSourceModel, DataTypeValue, SelectedSourceColumn } from '@facility/dataSources/types';
import { getProviderDisplayName, getSourceDataType, getSourceObjectDisplay, normalizeDataType } from '@facility/dataSources/utilities';

export interface ExternalAttributeColumn {
    Name: string;
    Identifier: string;
    DataType: DataTypeValue;
    SourceDataType: DataTypeValue;
}

export interface ExternalSourceCandidate {
    ID: string;
    Name: string;
    TypeName: string;
    ProviderName: string;
    SourceObjectName: string;
    SourceConfig: DataSourceModel['SourceConfig'];
    FdwStatus?: number | null;
    FdwLastSyncedDate?: string | null;
    FdwLastSyncError?: string | null;
    Columns: ExternalAttributeColumn[];
}

export interface ExternalSourceLoadResult {
    Sources: ExternalSourceCandidate[];
    Error?: string;
}

export const ATTRIBUTE_DATA_TYPE = {
    String: 1,
    Date: 2,
    Link: 3,
    Boolean: 4,
    Number: 5,
} as const;

export const EXTERNAL_ATTRIBUTE_ASSET_MATCH_TARGET_TYPE = {
    AssetName: 1,
    AssetAttribute: 2,
} as const;

export const EXTERNAL_ATTRIBUTE_DRIFT_STATUS = {
    None: 0,
    MissingColumn: 1,
    TypeChanged: 2,
} as const;

export function toAttributeDataType(dataType: DataTypeValue): AttributeDataType {
    switch (dataType) {
        case 'date':
            return ATTRIBUTE_DATA_TYPE.Date as AttributeDataType;
        case 'boolean':
            return ATTRIBUTE_DATA_TYPE.Boolean as AttributeDataType;
        case 'number':
            return ATTRIBUTE_DATA_TYPE.Number as AttributeDataType;
        case 'url':
            return ATTRIBUTE_DATA_TYPE.Link as AttributeDataType;
        case 'text':
        default:
            return ATTRIBUTE_DATA_TYPE.String as AttributeDataType;
    }
}

function toColumn(column: SelectedSourceColumn): ExternalAttributeColumn {
    return {
        Name: column.name,
        Identifier: column.identifier,
        DataType: normalizeDataType(column.datatype),
        SourceDataType: getSourceDataType(column),
    };
}

export function toExternalSourceCandidate(source: DataSourceModel): ExternalSourceCandidate {
    return {
        ID: source.ID,
        Name: source.Name,
        TypeName: source.TypeName,
        ProviderName: getProviderDisplayName([], source.Type, source.TypeName),
        SourceObjectName: getSourceObjectDisplay(source.SourceConfig),
        SourceConfig: source.SourceConfig,
        FdwStatus: source.FdwStatus,
        FdwLastSyncedDate: source.FdwLastSyncedDate,
        FdwLastSyncError: source.FdwLastSyncError,
        Columns: (source.SourceConfig?.columns || []).map(toColumn),
    };
}
